/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

/**
 * The `MfaStatus` enum mirrors the values of the `Mfa-Status` response header
 * returned by `POST /login` on a `202 Accepted` response, identifying whether the
 * user must enroll a factor or complete a Time-based One-time Password (TOTP)
 * challenge.
 */
export enum MfaStatus {
  Enroll = 'MFA_ENROLL',
  Required = 'MFA_REQUIRED'
}

/**
 * The only Multi-Factor Authentication (MFA) factor type supported by this release.
 * Push-based factors are intentionally excluded.
 */
export const TOTP_FACTOR_TYPE = 'token:software:totp';

export type TotpFactorType = typeof TOTP_FACTOR_TYPE;

/**
 * The supported TOTP authenticator providers.
 */
export type MfaProvider = 'OKTA' | 'GOOGLE';

/**
 * A single enrollable TOTP factor offered in the `MFA_ENROLL` catalog.
 */
export interface MfaEnrollmentFactor {
  link: string;
  factorType: TotpFactorType;
  provider: MfaProvider;
  stateToken: string;
  oktaUserId: string;
}

/**
 * The Okta factor metadata returned by `POST /mfa/enrollFactor`, carrying the QR
 * setup data required to complete enrollment.
 */
export interface OktaFactor {
  factorId: string;
  factorType: TotpFactorType;
  provider: MfaProvider;
  vendorName: string;
  status: 'PENDING_ACTIVATION';
  oktaUserId: string;
  qrCodeUrl: string;
  sharedSecret?: string;
}

export interface EnrollFactorResponse {
  oktaFactor: OktaFactor;
  intermediateToken?: string;
}

/**
 * Response of `POST /mfa/enableFactor`. Per the current contract it carries only a
 * message; an `authToken` is included only if the backend completes login on
 * activation (see ADR-0001 / release blockers).
 */
export interface EnableFactorResponse {
  message: string;
  authToken?: string;
  intermediateToken?: string;
}

/**
 * The returning-user TOTP challenge, derived from the `MFA_REQUIRED` login response.
 */
export interface MfaChallenge {
  stateToken: string;
  factorId: string;
  oktaUserId: string;
  expiresAt?: string;
}

export interface SubmitTotpResponse {
  authToken?: string;
  intermediateToken?: string;
}

export interface ResetMfaResponse {
  message?: string;
  emailHint?: string;
  intermediateToken?: string;
}

/**
 * The structured result of `authenticateMfa()`. The `status` discriminant tells the
 * caller whether login is complete, the user must enroll a factor, or the user must
 * answer a TOTP challenge. See `parseLoginResult` in `ConnectUtils`.
 */
export type LoginResult =
  | {
    status: 'authenticated';
    authToken: string;
  }
  | {
    status: 'enroll';
    intermediateToken: string;
    factors: MfaEnrollmentFactor[];
  }
  | {
    status: 'challenge';
    intermediateToken: string;
    stateToken: string;
    factorId: string;
    oktaUserId: string;
    expiresAt?: string;
  };

/**
 * Thrown when an in-flight MFA transaction cannot continue and must be restarted,
 * for example when an invalid passcode `403` returns no replacement intermediate
 * token, or a consumed token yields a `401 "Intermediate token has already been
 * used"`. Distinct from an invalid-passcode error so the passcode UI renders a
 * terminal "sign in again to restart" panel rather than another inline retry. It
 * lives in `core` so both the application authentication layer (which throws it)
 * and the feature-level passcode UI (which catches it) can share the type.
 */
export class MfaTransactionTerminalError extends Error {
  /* istanbul ignore next */
  public constructor(message = 'The MFA transaction can no longer continue.') {
    super(message);
    this.name = 'MfaTransactionTerminalError';
    // restore the prototype chain so `instanceof` holds when this file is
    // transpiled to a target that breaks `extends Error` (ES5 semantics) ...
    Object.setPrototypeOf(this, MfaTransactionTerminalError.prototype);
  }
};

/**
 * Thrown when `/mfa/totp` or `/mfa/enableFactor` rejects a previously-consumed
 * passcode (a `403 "Each code can only be used once"` response) but returns a
 * rotated intermediate token, so the transaction stays alive and retryable.
 * Distinct from a generic invalid-passcode error so the passcode UI can show a
 * "wait for a new code" hint instead of the generic invalid-code message, and
 * distinct from `MfaTransactionTerminalError` because the transaction can
 * still continue on the rotated token. It lives in `core` so both the
 * application authentication layer (which throws it) and the feature-level
 * passcode UI (which catches it) can share the type.
 */
export class MfaReusedCodeError extends Error {
  /* istanbul ignore next */
  public constructor(message = 'The MFA passcode was already used.') {
    super(message);
    this.name = 'MfaReusedCodeError';
    // restore the prototype chain so `instanceof` holds when this file is
    // transpiled to a target that breaks `extends Error` (ES5 semantics) ...
    Object.setPrototypeOf(this, MfaReusedCodeError.prototype);
  }
};

/**
 * Thrown when `/mfa/totp` or `/mfa/enableFactor` responds `429 Too Many Requests`,
 * meaning the identity provider has temporarily restricted the user after too
 * many invalid passcodes. The restriction is counted upstream and spans repeated
 * sign-ins and new enrollments, so it is distinct from both an invalid passcode
 * (retried inline) and `MfaTransactionTerminalError` (a consumed or expired
 * token): the passcode UI stops accepting submissions and asks the user to wait
 * out the cool-off period rather than immediately returning them to sign-in. It
 * lives in `core` so both the application authentication layer (which throws it)
 * and the feature-level passcode UI (which catches it) can share the type.
 */
export class MfaRateLimitError extends Error {
  /* istanbul ignore next */
  public constructor(message = 'Too many invalid MFA passcode attempts.') {
    super(message);
    this.name = 'MfaRateLimitError';
    // restore the prototype chain so `instanceof` holds when this file is
    // transpiled to a target that breaks `extends Error` (ES5 semantics) ...
    Object.setPrototypeOf(this, MfaRateLimitError.prototype);
  }
};
